/**
 * Cart Manager — The Spanish Alchemist
 * ─────────────────────────────────────────────────────────────────────────────
 * Single file replacing upsell-engine.js + gift-tier.js.
 *
 * Source of truth: /cart.js — fetched fresh on every sync.
 * No sessionStorage. No window flags. No cross-file events.
 *
 * Responsibilities:
 *   1. APP-ONLY 10%: auto-apply APP10 to every app cart with non-gift items,
 *      and keep it (and the old NEW20 / APP20) OUT of every browser cart
 *   2. Tag the cart with attribute source_app = android | ios | web
 *      (shows on every order in Shopify admin → "Additional details")
 *   3. Reconcile gift line items — 1 gift with every purchase, no tier conditions
 *   4. Render gift slots (with swap), discount notice, totals
 *   5. Checkout button goes straight to /checkout
 *   6. LINK GIFT (session offer): free cleanser for customers who open the
 *      OLD link (/discount/REGALOLIMPIADOR?redirect=…) or the new one
 *      (/products/<handle>?oferta=lmp) AND have a qualifying product in the
 *      cart. The offer lives in sessionStorage (this tab only, max
 *      LINK_GIFT.maxAgeMin minutes); when it is over, the cleanser and its
 *      code are removed from the cart. Added on top of the normal gift.
 *
 * App vs browser comes from window.TSA_APP, set in <head> by the TSA
 * detection script in layout/theme.liquid (store-app WebView + ?source= flag).
 *
 * Discount rule (app only): APP10 is applied silently on every sync when the
 * cart has at least one non-gift item and no other code is already active.
 * It is removed automatically when the cart is emptied.
 * A different manually-entered code (e.g. influencer code) takes priority —
 * APP10 will not overwrite it. In browsers, APP10 / APP20 / NEW20 are removed if
 * found in the cart; any other code (influencer etc.) is kept.
 *
 * Event contract (Maestroo native):
 *   Listens: cart:change (document), cart-drawer:refreshed (document)
 *   Uses:    cart:refresh on documentElement → triggers Maestroo drawer redraw
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════════
     CONFIG
  ═══════════════════════════════════════════════════════════════════════════ */

  var ROUTINE15_CODE    = 'ROUTINE15';
  var ROUTINE15_PCT     = 0.15;
  var MIN_PURCHASE_CENTS = 5000; // €50 — minimum for ROUTINE15 to apply
  var SHIPPING_CENTS    = 599;
  var SHIPPING_THRESHOLD = 5000; // €50

  /* App-only auto-apply coupon — silently applied to every non-empty APP cart.
     Must exist in Shopify admin → Discounts as an active "Amount off products"
     code (on the collection "APP20 – Productos con descuento"). */
  var AUTO_COUPON = 'APP10';

  /* Percentage of AUTO_COUPON — only used for the "−X%" label in the drawer's
     savings row. Keep in sync with the discount value in Shopify admin. */
  var AUTO_COUPON_PCT = 10;

  /* Old codes this file used to auto-apply (NEW20 = old sitewide 20%,
     NEW20WEB = single-product test, APP20 = old 20% app code). Treated as
     "ours": replaced by AUTO_COUPON in the app, removed in browsers. */
  var LEGACY_AUTO_COUPONS = ['NEW20', 'NEW20WEB', 'APP20'];

  /* Label shown in the cart drawer savings row instead of the code name */
  var AUTO_COUPON_LABEL = 'Descuento app';

  /* Max auto-apply attempts per page load (guards against retry loops if the
     code is ever missing / inactive in admin) */
  var MAX_AUTO_APPLY_TRIES = 2;

  /* ═══ LINK GIFT — free cleanser, only for customers on the special link ═══
     How it works:
       1. The offer STARTS (saved in sessionStorage = this tab only) when the
          customer lands through EITHER link:
            a) the OLD link  /discount/REGALOLIMPIADOR?redirect=/products/<handle>
               Shopify stores the code in the `discount_code` cookie; this file
               sees it, starts the offer and deletes the cookie (one-shot).
            b) the new link  /products/<handle>?oferta=lmp
          Nothing is added to the cart yet.
       2. When the offer is active AND the cart has a qualifying product, this
          file applies `codes[0]` to the cart and adds the cleanser (1 unit,
          _tsa_link_gift = 1). A product qualifies if its handle starts with
          one of `productPrefixes`, is in `productHandles`, OR (useTag) it
          carries the product tag `triggerTag` — the same tag that feeds the
          "Regalo limpiador – productos" collection used by the discount.
       3. The code (Buy X Get Y in admin) makes the cleanser €0 at checkout —
          price is enforced by Shopify, not by this file.
       4. Offer over (tab closed / time limit / not on the link) → on the next
          page load the cleanser AND the code are removed from the cart.
       5. Other tabs: the offer also counts while the link tab is still open
          (it sends a heartbeat every 20 s; other tabs wait 60 s of silence).
     The normal 1-gift-per-order logic below is NOT changed; this is extra.
     To switch off: enabled: false.  */
  var LINK_GIFT = {
    enabled         : true,
    param           : 'oferta',           // URL parameter name  ?oferta=lmp
    paramValue      : 'lmp',              // URL parameter value
    maxAgeMin       : 120,                // offer lifetime in minutes (backstop)
    heartbeatSec    : 20,                 // link tab "still open" signal
    heartbeatStaleSec: 60,                // other tabs: offer ends after this much silence
    codes           : ['REGALOLIMPIADOR'],   // codes[0] is applied; any case
    useTag          : true,               // also qualify products tagged `triggerTag`
    triggerTag      : 'regalo-limpiador', // product tag = in the offer (admin-managed)
    legacyLink      : true,               // old /discount/ link (discount_code cookie) starts the offer
    graceUntil      : '2026-10-31T23:59:00+01:00', // until then, a cart that ALREADY holds the code keeps its offer (customers mid-journey). Empty string = no grace.
    productPrefixes : ['anti-aging-baby-routine'], // handles that start with this qualify
    productHandles  : [                   // extra exact handles (products whose handle doesn't start with a prefix above)
      'rutina-esencial-para-arrugas-y-manchas',
      'rutina-esencial-para-arrugas-y-manchas-para-piel-sensible',
      'rutina-esencial-para-arrugas-y-manchas-set-de-mascarillas-para-piel-sensible',
      'vitamin-c-anti-aging-essential-routine-with-pigment-correction-serum-and-masks-face-under-eye-oily-copy',
      'vitamin-c-anti-aging-essential-routine-with-peptides-orchid-stem-cells-collagen-boosting-algae-copy',
      'pigment-correction-baby-routine'
    ],
    giftHandle      : 'cleanser-oily-combination', // Limpiador en Gel Universal con Niacinamida al 3%
    giftVariantId   : null,               // optional; null = read from the product handle
    giftName        : 'Limpiador en Gel Universal',
    maxTries        : 2                   // add / apply attempts per page load
  };

  /**
   * Gift logic — 1 free gift per order, variant resolved from cart contents.
   *   AA Serum  → AA Cream mini      AA Cream → AA Serum mini
   *   HP Serum  → HP Cream mini      HP Cream → HP Serum mini
   *   AA Routine → HP Serum mini     HP Routine → AA Serum mini
   *   Other     → AA Serum mini (fallback)
   * Priority: lowest-priced non-gift item triggers first.
   */
  var GIFT_VARIANTS = {
    AA_SERUM : 53624342053191,
    AA_CREAM : 53624388026695,
    HP_SERUM : 53624359256391,
    HP_CREAM : 53624401396039,
  };

  var GIFT_VARIANT_IDS = {};
  Object.keys(GIFT_VARIANTS).forEach(function (k) { GIFT_VARIANT_IDS[GIFT_VARIANTS[k]] = true; });

  var GIFT_FALLBACK_ORDER = [
    GIFT_VARIANTS.AA_SERUM, GIFT_VARIANTS.HP_SERUM,
    GIFT_VARIANTS.AA_CREAM, GIFT_VARIANTS.HP_CREAM,
  ];

  var GIFT_TRIGGER_MAP = (function () {
    var m = {};
    [10635980144967, 10635980177735, 10496187957575].forEach(function (id) { m[id] = GIFT_VARIANTS.AA_CREAM; });
    [10635980210503, 10496209977671, 10496210993479].forEach(function (id) { m[id] = GIFT_VARIANTS.AA_SERUM; });
    [10635980276039, 10635980308807, 10496193233223].forEach(function (id) { m[id] = GIFT_VARIANTS.HP_CREAM; });
    [10635980341575, 10635980407111, 10496214303047].forEach(function (id) { m[id] = GIFT_VARIANTS.HP_SERUM; });
    [10635980734791, 10635980767559, 10635980833095,
     10635980964167, 10635980996935, 10635981029703].forEach(function (id) { m[id] = GIFT_VARIANTS.HP_SERUM; });
    [10635980865863, 10635980898631, 10635980931399,
     10635981062471, 10635981095239, 10635981128007].forEach(function (id) { m[id] = GIFT_VARIANTS.AA_SERUM; });
    return m;
  }());

  function resolveGiftVariant(cart) {
    var items = cart.items
      .filter(function (i) { return !isGiftItem(i); })
      .slice().sort(function (a, b) { return a.price - b.price; });
    for (var i = 0; i < items.length; i++) {
      var gv = GIFT_TRIGGER_MAP[items[i].product_id];
      if (gv) return gv;
    }
    return GIFT_FALLBACK_ORDER[0];
  }


  // Tiers kept for reference but not used — 1 gift with every purchase
  var TIERS = [
    { min: 7500, count: 3 },
    { min: 6500, count: 2 },
    { min: 5500, count: 1 },
    { min: 0,    count: 0 },
  ];

  // Milestones kept for reference but progress bar is paused
  var MILESTONES = [
    { threshold: 5000, label: 'Free Shipping' },
    { threshold: 5500, label: 'Gift 1'        },
    { threshold: 6500, label: 'Gift 2'        },
    { threshold: 7500, label: 'Gift 3'        },
  ];

  /**
   * All qualifying products for ROUTINE15.
   * handle → { line, form, skin, price (cents), name }
   */
  var PRODUCT_MAP = {
    // ── AA Serums ─────────────────────────────────────────────────────────
    'ageless-serum-oily-combination': { line: 'aa', form: 'serum', skin: 'oily',      price: 3900, name: 'Ageless Lift Serum'         },
    'ageless-serum-dry':              { line: 'aa', form: 'serum', skin: 'dry',       price: 3900, name: 'Ageless Lift Serum'         },
    'ageless-serum-sensitive':        { line: 'aa', form: 'serum', skin: 'sensitive', price: 3900, name: 'Ageless Lift Serum'         },
    // ── AA Creams ─────────────────────────────────────────────────────────
    'ageless-moisturizer-oily-combination': { line: 'aa', form: 'cream', skin: 'oily',      price: 3100, name: 'Ageless Lift Moisturizer'     },
    'ageless-moisturizer-dry':              { line: 'aa', form: 'cream', skin: 'dry',       price: 3100, name: 'Ageless Lift Moisturizer'     },
    'ageless-moisturizer-sensitive':        { line: 'aa', form: 'cream', skin: 'sensitive', price: 3100, name: 'Ageless Lift Moisturizer'     },
    // ── HP Serums ─────────────────────────────────────────────────────────
    'even-tone-serum-oily-combination': { line: 'hp', form: 'serum', skin: 'oily',      price: 4200, name: 'Even-Tone Glow Serum'        },
    'even-tone-serum-dry':              { line: 'hp', form: 'serum', skin: 'dry',       price: 4200, name: 'Even-Tone Glow Serum'        },
    'even-tone-serum-sensitive':        { line: 'hp', form: 'serum', skin: 'sensitive', price: 4200, name: 'Even-Tone Glow Serum'        },
    // ── HP Creams ─────────────────────────────────────────────────────────
    'even-tone-moisturizer-oily-combination': { line: 'hp', form: 'cream', skin: 'oily',      price: 3200, name: 'Even-Tone Glow Moisturizer'  },
    'even-tone-moisturizer-dry':              { line: 'hp', form: 'cream', skin: 'dry',       price: 3200, name: 'Even-Tone Glow Moisturizer'  },
    'even-tone-moisturizer-sensitive':        { line: 'hp', form: 'cream', skin: 'sensitive', price: 3200, name: 'Even-Tone Glow Moisturizer'  },
    // ── AC Serums ─────────────────────────────────────────────────────────
    'bye-breakouts-serum-oily-combination': { line: 'ac', form: 'serum', skin: 'oily',      price: 3300, name: 'Bye Breakouts Serum'         },
    'bye-breakouts-serum-dry':              { line: 'ac', form: 'serum', skin: 'dry',       price: 3300, name: 'Bye Breakouts Serum'         },
    'bye-breakouts-serum-sensitive':        { line: 'ac', form: 'serum', skin: 'sensitive', price: 3300, name: 'Bye Breakouts Serum'         },
    // ── AC Creams ─────────────────────────────────────────────────────────
    'bye-breakouts-moisturizer-oily-combination': { line: 'ac', form: 'cream', skin: 'oily',      price: 2500, name: 'Bye Breakouts Moisturizer'   },
    'bye-breakouts-moisturizer-dry':              { line: 'ac', form: 'cream', skin: 'dry',       price: 2500, name: 'Bye Breakouts Moisturizer'   },
    'bye-breakouts-moisturizer-sensitive':        { line: 'ac', form: 'cream', skin: 'sensitive', price: 2500, name: 'Bye Breakouts Moisturizer'   },
  };

  var LINE_NAMES = { aa: 'Ageless Lift', hp: 'Even-Tone Glow', ac: 'Bye Breakouts' };
  var SKIN_NAMES = { oily: 'Oily/Combination', dry: 'Dry', sensitive: 'Sensitive' };

  // Build companion map: 'aa-oily' → { serum: handle, cream: handle }
  var COMPANION_MAP = {};
  Object.keys(PRODUCT_MAP).forEach(function (handle) {
    var p   = PRODUCT_MAP[handle];
    var key = p.line + '-' + p.skin;
    if (!COMPANION_MAP[key]) COMPANION_MAP[key] = {};
    COMPANION_MAP[key][p.form] = handle;
  });

  /* ═══════════════════════════════════════════════════════════════════════════
     STATE  — minimal, never persisted
  ═══════════════════════════════════════════════════════════════════════════ */

  var isReconciling  = false; // prevents concurrent gift reconciliation
  var refreshPending = false; // true while we're waiting for Maestroo to re-render
  var variantCache   = {};    // handle → variantId (for upsell ATC)
  var imageCache     = {};    // handle → image URL  (for upsell card)
  var autoApplyTries = 0;     // APP10 apply attempts this page load
  var linkCodeTries  = 0;     // link-code (re)apply attempts this page load
  var linkAddTries   = 0;     // cleanser add attempts this page load
  var tagStore       = {};    // product handle → [lowercase tags] (filled by primeOfferTags)

  /* One sync at a time. Events that arrive while a sync is running (the theme
     fires cart events after OUR OWN cart writes too) are coalesced into a
     single follow-up check instead of starting overlapping syncs. */
  var syncRunning    = false;
  var syncQueued     = false;
  var syncStartedAt  = 0;
  var lastCartSig    = null;

  /* ═══ FAST ADD — gifts + coupon travel WITH the product ═══════════════════
     On a product page, when the customer adds the product, this file puts the
     free mini gift and (when the offer is active) the cleanser into the SAME
     /cart/add.js request, applies the coupon right after, and only then hands
     the answer back to the theme — so the drawer opens ONCE with everything
     in place. Anything it cannot predict (other pages, odd request formats,
     refused add) falls back to the normal flow, so nothing can break the add.
     To switch off: enabled: false.  */
  var FAST_ADD = {
    enabled          : true,
    maxKnownCartAgeMs: 20000   // older than this → re-read the cart before predicting
  };
  var knownCart      = null;    // last cart we saw (kept by fetchCart)
  var knownCartAt    = 0;
  var fastAddBusy    = false;

  /* The customer clicked × on the code chip → don't put the code back (and the
     cleanser stays out) until a fresh link click, or until the qualifying
     product has left the cart and comes back. Session only. */
  var DECLINE_KEY    = 'tsa_lo_off';
  var linkSeen       = false;   // link code was on the cart at the last sync
  var prevOtherCodes = [];      // other (non-link, non-APP10) codes at the last sync
  var MAX_SYNC_PASSES = 3;    // passes inside ONE sync before the single refresh

  /* Debug trace: add ?tsadebug=1 to the URL (or run localStorage.tsa_debug='1')
     and open the console — every pass / refresh is logged. Off by default. */
  var DEBUG = false;
  try { DEBUG = /[?&]tsadebug=1/.test(window.location.search) || window.localStorage.getItem('tsa_debug') === '1'; } catch (e) {}
  function dbg() {
    if (!DEBUG) return;
    var a = ['[cart-manager]', Math.round((window.performance && performance.now()) || 0) + 'ms'];
    for (var i = 0; i < arguments.length; i++) a.push(arguments[i]);
    try { console.log.apply(console, a); } catch (e) {}
  }
  var isLinkReconciling = false;

  /* ═══════════════════════════════════════════════════════════════════════════
     HELPERS
  ═══════════════════════════════════════════════════════════════════════════ */

  function extractHandle(item) {
    var m = item.url && item.url.match(/\/products\/([^?#]+)/);
    return m ? m[1] : null;
  }

  function isGiftItem(item) {
    /* Travel minis are purchasable — never classify as free gift regardless of variant ID */
    if (item.properties && item.properties['_travel_mini'] === '1') return false;
    if (isLinkGiftItem(item)) return true;
    if (item.properties && item.properties._is_gift === 'true') return true;
    return !!GIFT_VARIANT_IDS[item.variant_id];
  }

  /* The link cleanser — counts as a gift for subtotals, but is handled by
     reconcileLinkGift(), never by the normal 1-gift reconciliation */
  function isLinkGiftItem(item) {
    return !!(item.properties && item.properties._tsa_link_gift === '1');
  }

  function isRegularGift(item) {
    return isGiftItem(item) && !isLinkGiftItem(item);
  }

  function isLinkGiftCode(code) {
    if (!LINK_GIFT.enabled || !code) return false;
    var c = String(code).toUpperCase();
    return LINK_GIFT.codes.some(function (l) { return String(l).toUpperCase() === c; });
  }

  function cartHasLinkCode(cart) {
    return (cart.discount_codes || []).some(function (d) { return isLinkGiftCode(d.code); });
  }

  /* Shopify's /discount/CODE link stores the code in the discount_code cookie.
     offerInit() reads it once to start the session offer (old link), then
     deletes it. The code is never restored from it. */
  function getCookieLinkCode() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!m) return null;
    var code;
    try { code = decodeURIComponent(m[1]); } catch (e) { code = m[1]; }
    return isLinkGiftCode(code) ? code : null;
  }

  /* Discount on the cleanser line (the €0 from the link code) — kept out of
     the coupon / APP10 saving numbers so they stay correct */
  function linkGiftDiscount(cart) {
    return cart.items.reduce(function (sum, i) {
      if (!isLinkGiftItem(i)) return sum;
      return sum + Math.max(0, i.original_line_price - i.final_line_price);
    }, 0);
  }

  function discountExclLink(cart) {
    return Math.max(0, (cart.total_discount || 0) - linkGiftDiscount(cart));
  }

  function clearLinkCookie() {
    if (!getCookieLinkCode()) return;
    var host = window.location.hostname;
    var parts = host.split('.');
    var root = parts.length > 2 ? parts.slice(-2).join('.') : host;
    var exp = 'discount_code=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = exp;
    document.cookie = exp + '; domain=' + host;
    document.cookie = exp + '; domain=.' + root;
  }

  /* ── Session offer ─────────────────────────────────────────────────────────
     sessionStorage  tsa_lo      = { s: <start ms> }       this tab only
     localStorage    tsa_lo_hb   = { s: <start ms>, t: <last beat ms> }
                                   written by the link tab so other tabs know
                                   the offer is still open somewhere        */
  var OFFER_KEY = 'tsa_lo';
  var HB_KEY    = 'tsa_lo_hb';

  function readJSON(store, key) {
    try { return JSON.parse(store.getItem(key) || 'null'); } catch (e) { return null; }
  }

  function offerFresh(startMs) {
    return !!startMs && (Date.now() - startMs) < LINK_GIFT.maxAgeMin * 60000;
  }

  function ownOfferStart() {
    var o = readJSON(window.sessionStorage, OFFER_KEY);
    return (o && offerFresh(o.s)) ? o.s : 0;
  }

  /* Customers who already hold the code in their cart when this version goes
     live keep their offer until `graceUntil` (no session exists for them). */
  function graceActive(cart) {
    var g = Date.parse(LINK_GIFT.graceUntil || '');
    return !!(g && Date.now() < g && cart && cartHasLinkCode(cart));
  }

  function offerActive(cart) {
    if (!LINK_GIFT.enabled) return false;
    if (graceActive(cart)) return true;
    try {
      if (ownOfferStart()) return true;
      var hb = readJSON(window.localStorage, HB_KEY);
      return !!(hb && offerFresh(hb.s) && (Date.now() - hb.t) < LINK_GIFT.heartbeatStaleSec * 1000);
    } catch (e) {
      return false;
    }
  }

  function beat() {
    try {
      var s = ownOfferStart();
      if (!s) return;
      window.localStorage.setItem(HB_KEY, JSON.stringify({ s: s, t: Date.now() }));
    } catch (e) {}
  }

  /* Runs once, as early as possible: landing on ?oferta=lmp starts the offer
     in THIS tab; a tab that already has one keeps it alive with a heartbeat. */
  function linkDeclined() {
    try { return window.sessionStorage.getItem(DECLINE_KEY) === '1'; } catch (e) { return false; }
  }
  function setLinkDeclined(on) {
    try {
      if (on) window.sessionStorage.setItem(DECLINE_KEY, '1');
      else window.sessionStorage.removeItem(DECLINE_KEY);
    } catch (e) {}
    dbg('link offer declined by customer:', !!on);
  }

  function offerInit() {
    if (!LINK_GIFT.enabled) return;
    try {
      var q = new URLSearchParams(window.location.search);
      if (String(q.get(LINK_GIFT.param) || '').toLowerCase() === String(LINK_GIFT.paramValue).toLowerCase()) {
        window.sessionStorage.setItem(OFFER_KEY, JSON.stringify({ s: Date.now() }));
        setLinkDeclined(false);
      }
    } catch (e) {}

    /* OLD link: Shopify's /discount/CODE redirect leaves the code in the
       discount_code cookie. Treat it as a fresh link click: start the offer
       in this tab, then DELETE the cookie so it can't start another session
       later (a new click sets it again). */
    if (LINK_GIFT.legacyLink && getCookieLinkCode()) {
      try {
        if (!ownOfferStart()) {
          window.sessionStorage.setItem(OFFER_KEY, JSON.stringify({ s: Date.now() }));
        }
        setLinkDeclined(false);
      } catch (e) {}
      clearLinkCookie();
    }

    if (ownOfferStart()) {
      beat();
      setInterval(beat, LINK_GIFT.heartbeatSec * 1000);
      /* warm the cleanser's variant id now, so adding it later needs no extra request */
      if (!LINK_GIFT.giftVariantId) {
        setTimeout(function () { fetchVariantId(LINK_GIFT.giftHandle).catch(function () {}); }, 800);
      }
    }
  }

  /* Does this handle belong to the offer (AA Baby Routine)? */
  function isOfferHandle(handle) {
    if (!handle) return false;
    var h = String(handle).toLowerCase();
    if (LINK_GIFT.productHandles.some(function (x) { return String(x).toLowerCase() === h; })) return true;
    return LINK_GIFT.productPrefixes.some(function (p) { return h.indexOf(String(p).toLowerCase()) === 0; });
  }

  function itemQualifies(item) {
    if (isGiftItem(item)) return false;
    var h = extractHandle(item);
    if (!h) return false;
    if (isOfferHandle(h)) return true;
    if (LINK_GIFT.useTag) {
      var tags = tagStore[h.toLowerCase()];
      return !!(tags && tags.indexOf(String(LINK_GIFT.triggerTag).toLowerCase()) !== -1);
    }
    return false;
  }

  /* Cart holds at least one paid qualifying product (synchronous — tags come
     from tagStore, which primeOfferTags() fills before the check runs) */
  function hasTriggerProduct(cart) {
    return cart.items.some(itemQualifies);
  }

  /* Fetch tags (/products/<handle>.js) of cart products we don't know yet.
     Runs only while an offer is active or the cart holds the code, so normal
     customers never trigger these requests. */
  function primeOfferTags(cart) {
    if (!LINK_GIFT.enabled || !LINK_GIFT.useTag || !cart || !cart.items) return Promise.resolve();
    if (!offerActive(cart) && !cartHasLinkCode(cart)) return Promise.resolve();
    var seen = {};
    var todo = [];
    cart.items.forEach(function (i) {
      if (isGiftItem(i)) return;
      var h = extractHandle(i);
      if (!h) return;
      h = h.toLowerCase();
      if (seen[h] || tagStore[h] || isOfferHandle(h)) return;
      seen[h] = true;
      todo.push(h);
    });
    return Promise.all(todo.map(function (h) {
      return fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          tagStore[h] = ((p && p.tags) || []).map(function (x) { return String(x).toLowerCase(); });
        })
        .catch(function () { tagStore[h] = []; });
    })).then(function () {});
  }

  /**
   * Detects whether the cart contains a complete serum+cream pair
   * (same line, same skin type). Computed fresh from cart data every time.
   */
  function detectPair(cart) {
    var lineSkInCart = {};
    cart.items.forEach(function (item) {
      if (isGiftItem(item)) return;
      var handle = extractHandle(item);
      if (!handle || !PRODUCT_MAP[handle]) return;
      var p   = PRODUCT_MAP[handle];
      var key = p.line + '-' + p.skin;
      if (!lineSkInCart[key]) lineSkInCart[key] = {};
      lineSkInCart[key][p.form] = true;
    });
    return Object.keys(lineSkInCart).some(function (key) {
      return lineSkInCart[key].serum && lineSkInCart[key].cream;
    });
  }

  /**
   * ROUTINE15 saving in cents, calculated from original_line_price.
   * Independent of what Shopify has in cart.total_price.
   */
  function calcRoutine15Saving(cart) {
    var qualifyingCents = 0;
    cart.items.forEach(function (item) {
      if (isGiftItem(item)) return;
      var handle = extractHandle(item);
      if (handle && PRODUCT_MAP[handle]) {
        qualifyingCents += item.original_line_price;
      }
    });
    var rawSubtotal = cart.items.reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.original_line_price;
    }, 0);
    if (rawSubtotal < MIN_PURCHASE_CENTS) return 0;
    return Math.round(qualifyingCents * ROUTINE15_PCT);
  }

  /**
   * Effective subtotal for gift tier calculation.
   * Stacks discounts correctly:
   *   1. ROUTINE15 (15% on qualifying items if pair detected)
   *   2. Manual code applied on the post-ROUTINE15 value
   */
  function calcEffectiveSubtotal(cart, pairDetected) {
    var rawSubtotal     = cart.items.reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.original_line_price;
    }, 0);
    var routine15Saving = pairDetected ? calcRoutine15Saving(cart) : 0;
    var postRoutine15   = rawSubtotal - routine15Saving;

    var manualCode = getManualCode(cart);
    var manualDisc = discountExclLink(cart);
    if (manualCode && manualDisc > 0 && rawSubtotal > 0) {
      var manualPct = manualDisc / rawSubtotal;
      return Math.round(postRoutine15 * (1 - manualPct));
    }
    return postRoutine15;
  }

  function getTierCount(subtotalCents) {
    for (var i = 0; i < TIERS.length; i++) {
      if (subtotalCents >= TIERS[i].min) return TIERS[i].count;
    }
    return 0;
  }

  function getProgressState(subtotal) {
    var lastThresh = MILESTONES[MILESTONES.length - 1].threshold;
    if (subtotal >= lastThresh) {
      var giftCount = getTierCount(subtotal);
      return {
        progress: 1,
        message: '🎉 Free shipping + all <strong>' + giftCount + ' free gifts</strong> unlocked!',
      };
    }
    var activeIdx  = 0;
    for (var i = 0; i < MILESTONES.length; i++) {
      if (subtotal < MILESTONES[i].threshold) { activeIdx = i; break; }
    }
    var prevThresh = activeIdx === 0 ? 0 : MILESTONES[activeIdx - 1].threshold;
    var nextThresh = MILESTONES[activeIdx].threshold;
    var progress   = Math.min(1, Math.max(0, (subtotal - prevThresh) / (nextThresh - prevThresh)));
    var needed     = formatMoney(nextThresh - subtotal);
    var giftCount  = getTierCount(subtotal);
    var hasShipping = subtotal >= SHIPPING_THRESHOLD;
    var message;
    if (!hasShipping) {
      message = 'Add <strong>' + needed + '</strong> more for <strong>free shipping</strong> 🚚';
    } else if (giftCount === 0) {
      message = '🚚 Free shipping unlocked! Add <strong>' + needed + '</strong> more for a <strong>free gift</strong> 🎁';
    } else {
      message = 'Add <strong>' + needed + '</strong> more to unlock your <strong>' + ordinal(giftCount + 1) + ' free gift</strong> 🎁';
    }
    return { progress: progress, message: message };
  }

  /**
   * Returns whether a manual (non-ROUTINE15) discount code is applied in the cart.
   */
  function getManualCode(cart) {
    if (!cart.discount_codes || !cart.discount_codes.length) return null;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var d = cart.discount_codes[i];
      if (d.code !== ROUTINE15_CODE && !isLinkGiftCode(d.code) && d.applicable) return d.code;
    }
    return null;
  }

  function isRoutine15InCart(cart) {
    if (!cart.discount_codes) return false;
    return cart.discount_codes.some(function (d) {
      return d.code === ROUTINE15_CODE && d.applicable;
    });
  }

  function formatMoney(cents) {
    var euros = cents / 100;
    return '€' + (euros % 1 === 0 ? euros.toFixed(0) : euros.toFixed(2));
  }

  function ordinal(n) {
    var s = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function escAttr(str) { return String(str).replace(/"/g, '&quot;'); }

  /* ═══════════════════════════════════════════════════════════════════════════
     CART API CALLS
  ═══════════════════════════════════════════════════════════════════════════ */

  function fetchCart() {
    return fetch(Shopify.routes.root + 'cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (c) { knownCart = c; knownCartAt = Date.now(); return c; });
  }

  function applyCartDiscount(code) {
    return fetch(Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-TSA-Internal': '1' },
      body: JSON.stringify({ discount: code }),
    }).then(function (r) { return r.json(); });
  }

  /* cart/update.js already answers with the updated cart — only fall back to a
     separate GET if the answer doesn't look like a cart */
  function cartOrFetch(c) {
    return (c && c.items && c.discount_codes) ? c : fetchCart();
  }

  function addGift(variantId) {
    return fetch(Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _is_gift: 'true' } }],
      }),
    }).then(function (r) { return r.json(); });
  }

  /* Cleanser variant id if we already know it (prefetched when the offer starts) */
  function linkGiftVariantNow() {
    return LINK_GIFT.giftVariantId || variantCache[LINK_GIFT.giftHandle] || null;
  }

  /* Would reconcileLinkGift add the cleanser to this cart? (same rule, used to
     put it in the SAME request as the regular gift) */
  function linkNeedsAdd(cart) {
    if (!LINK_GIFT.enabled || isLinkReconciling || !cart || !cart.items) return false;
    if (cart.items.some(isLinkGiftItem)) return false;
    if (linkAddTries >= LINK_GIFT.maxTries) return false;
    return !!(cartHasLinkCode(cart) &&
              cart.items.some(function (i) { return !isGiftItem(i); }) &&
              offerActive(cart) && hasTriggerProduct(cart));
  }

  /* Regular gift + (when due) the cleanser in ONE cart/add.js — one request and
     one cart re-read instead of two. If the combined add is refused (e.g. the
     cleanser is out of stock) the regular gift is still added on its own. */
  function addGiftBatched(variantId, cart) {
    var lv = linkNeedsAdd(cart) ? linkGiftVariantNow() : null;
    if (!lv) return addGift(variantId);
    linkAddTries++;
    return fetch(Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          { id: variantId, quantity: 1, properties: { _is_gift: 'true' } },
          { id: lv,        quantity: 1, properties: { _is_gift: 'true', _tsa_link_gift: '1' } },
        ],
      }),
    }).then(function (r) {
      if (r.ok) return r.json();
      dbg('batched add refused → regular gift only');
      return addGift(variantId);
    });
  }

  function removeGift(lineKey) {
    return fetch(Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    }).then(function (r) { return r.json(); });
  }

  function fetchVariantId(handle) {
    if (variantCache[handle]) return Promise.resolve(variantCache[handle]);
    return fetch(Shopify.routes.root + 'products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        variantCache[handle] = data.variants[0].id;
        if (data.featured_image) imageCache[handle] = data.featured_image;
        else if (data.images && data.images[0]) imageCache[handle] = data.images[0];
        return variantCache[handle];
      });
  }

  function fetchProductImage(handle) {
    if (imageCache[handle]) return Promise.resolve(imageCache[handle]);
    return fetch(Shopify.routes.root + 'products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var img = data.featured_image || (data.images && data.images[0]) || null;
        if (img) imageCache[handle] = img;
        return img;
      })
      .catch(function () { return null; });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     APP vs BROWSER CONTEXT
     window.TSA_APP is set in <head> by layout/theme.liquid. Missing → browser.
  ═══════════════════════════════════════════════════════════════════════════ */

  function isAppContext() {
    return !!(window.TSA_APP && window.TSA_APP.isApp === true);
  }

  function appSource() {
    if (!isAppContext()) return 'web';
    var s = window.TSA_APP.source;
    return (s === 'android' || s === 'ios') ? s : 'web';
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     CART ATTRIBUTE — source_app = android | ios | web
     Written only when the cart has items and the value differs, so it costs
     one request per cart, not per page.
  ═══════════════════════════════════════════════════════════════════════════ */

  function ensureSourceAttribute(cart) {
    if (!cart || !cart.item_count) return Promise.resolve(cart);
    var src = appSource();
    var current = cart.attributes && cart.attributes.source_app;
    if (current === src) return Promise.resolve(cart);

    return fetch(Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { source_app: src } }),
    })
      .then(function (r) { return r.json(); })
      .then(function (fresh) { return (fresh && fresh.items) ? fresh : cart; })
      .catch(function () { return cart; });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     DISCOUNT MANAGEMENT — APP-ONLY AUTO-APPLY
     APP:     applies AUTO_COUPON to every cart that has at least one non-gift
              item. Will not overwrite a different code the customer has
              entered. Replaces a leftover legacy code (NEW20). Clears when the
              cart is emptied.
     BROWSER: never applies it; removes AUTO_COUPON / legacy codes if present,
              keeping any other code (influencer etc.).
     Resolves to { cart, changed } — changed = the discount was touched, so the
     drawer's server-rendered line prices need a redraw.
  ═══════════════════════════════════════════════════════════════════════════ */

  function codeUpper(c) { return String(c || '').toUpperCase(); }

  function isOurCode(code) {
    var c = codeUpper(code);
    if (c === codeUpper(AUTO_COUPON)) return true;
    return LEGACY_AUTO_COUPONS.some(function (l) { return codeUpper(l) === c; });
  }

  function isAutoCouponLabel(code) {
    return isOurCode(code);
  }

  function ensureDiscountState(cart) {
    var codes        = cart.discount_codes || [];
    var nonGiftItems = cart.items.filter(function (i) { return !isGiftItem(i); });
    var hasItems     = nonGiftItems.length > 0;

    /* Three groups: APP10 / legacy ("ours"), the link-gift code, and every
       other code the customer entered (influencer etc.). The link-gift code
       is never removed and never blocks APP10. */
    var ourCodes   = codes.filter(function (d) { return isOurCode(d.code); });
    var linkCodes  = codes.filter(function (d) { return isLinkGiftCode(d.code); });
    var otherCodes = codes.filter(function (d) { return !isOurCode(d.code) && !isLinkGiftCode(d.code); });
    var otherApplicable = otherCodes.some(function (d) { return d.applicable; });
    var autoPresent = codes.some(function (d) { return codeUpper(d.code) === codeUpper(AUTO_COUPON); });
    var legacyPresent = ourCodes.some(function (d) { return codeUpper(d.code) !== codeUpper(AUTO_COUPON); });

    var otherList = otherCodes.map(function (d) { return d.code; });
    var linkList  = linkCodes.map(function (d) { return d.code; });

    /* Session offer: the link code belongs on the cart only while the offer
       is active AND an AA Baby Routine product is in the cart. Otherwise it
       is removed (and the old discount_code cookie is cleared). */
    var offerOn    = offerActive(cart);
    var hasTrig    = !!(hasItems && hasTriggerProduct(cart));
    var wantedBase = !!(LINK_GIFT.enabled && offerOn && hasTrig);

    /* Customer removed the code chip (not us, not a new coupon typed in)? */
    var newOther = otherList.some(function (c) {
      return prevOtherCodes.indexOf(codeUpper(c)) === -1;
    });
    if (!hasTrig && linkDeclined()) setLinkDeclined(false);
    if (!linkList.length && linkSeen && wantedBase && !newOther && !linkDeclined()) {
      setLinkDeclined(true);
    }
    linkSeen       = linkList.length > 0;
    prevOtherCodes = otherList.map(codeUpper);

    var linkWanted = wantedBase && !linkDeclined();
    var linkMissing = false;
    var linkStale   = false;

    if (!offerOn) clearLinkCookie();

    if (linkWanted) {
      if (!linkList.length && linkCodeTries < LINK_GIFT.maxTries) {
        linkList.push(LINK_GIFT.codes[0]);
        linkMissing = true;
      }
    } else if (linkList.length && linkCodeTries < LINK_GIFT.maxTries) {
      linkList = [];
      linkStale = true;
      linkSeen  = false;
    }
    var linkChange = linkMissing || linkStale;

    function joinCodes(list) {
      var seen = {};
      return list.filter(function (c) {
        var k = codeUpper(c);
        if (!c || seen[k]) return false;
        seen[k] = true;
        return true;
      }).join(',');
    }

    function done(fresh) { return { cart: fresh, changed: true }; }
    function unchanged() { return Promise.resolve({ cart: cart, changed: false }); }

    if (isAppContext()) {
      var wantAuto = hasItems && !otherApplicable;
      var needAuto = wantAuto && (!autoPresent || legacyPresent) && autoApplyTries < MAX_AUTO_APPLY_TRIES;

      /* APP — apply APP10 (no customer code) and/or restore the link code */
      if (needAuto || linkChange) {
        if (needAuto) autoApplyTries++;
        if (linkChange) linkCodeTries++;
        var list = (wantAuto ? [AUTO_COUPON] : []).concat(wantAuto ? [] : otherList).concat(linkList);
        return applyCartDiscount(joinCodes(list)).then(cartOrFetch).then(done);
      }

      /* APP — cart emptied but our code still set → clear it */
      if (!hasItems && ourCodes.length) {
        return applyCartDiscount(joinCodes(otherList.concat(linkList))).then(cartOrFetch).then(done);
      }

      return unchanged();
    }

    /* BROWSER — the app discount must never be in a browser cart;
       the link-gift code is added / removed with the session offer */
    if (ourCodes.length || linkChange) {
      if (linkChange) linkCodeTries++;
      return applyCartDiscount(joinCodes(otherList.concat(linkList))).then(cartOrFetch).then(done);
    }

    return unchanged();
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     GIFT RECONCILIATION — 1 gift with every purchase, no tier conditions
  ═══════════════════════════════════════════════════════════════════════════ */

  function reconcileGifts(passedCart, isFresh) {
    if (isReconciling) return Promise.resolve({ modified: false, cart: passedCart });
    isReconciling = true;

    /* Always fetch fresh cart state before deciding — this prevents the race
       condition where a stale cart (without the gift) is passed in from
       event detail, causing reconcileGifts to add the gift a second time,
       resulting in the double-gift → remove-all → add-one loop that makes
       the gift flicker and disappear. */
    /* Inside runSync() the cart handed in was fetched moments ago and syncs no
       longer overlap, so the extra GET is skipped (isFresh) — saves ~1 round trip. */
    var cartSource = (isFresh && passedCart && passedCart.items) ? Promise.resolve(passedCart) : fetchCart();
    return cartSource.then(function (cart) {

      var nonGift = cart.items.filter(function (i) { return !isGiftItem(i); });
      var gifts   = cart.items.filter(isRegularGift);   // link cleanser handled separately
      var rawSubtotal = nonGift.reduce(function (sum, i) { return sum + i.original_line_price; }, 0);
      var target  = (nonGift.length > 0 && rawSubtotal >= MIN_PURCHASE_CENTS) ? resolveGiftVariant(cart) : null;

      if (!target) {
        if (!gifts.length) {
          isReconciling = false;
          return { modified: false, cart: cart };
        }
        var p = Promise.resolve();
        gifts.forEach(function (g) { p = p.then(function () { return removeGift(g.key); }); });
        return p.then(fetchCart).then(function (fresh) {
          isReconciling = false;
          return { modified: true, cart: fresh };
        }).catch(function (e) {
          console.error('[cart-manager] gift remove failed', e);
          isReconciling = false;
          return { modified: false, cart: cart };
        });
      }

      if (gifts.length === 1 && gifts[0].variant_id === target) {
        isReconciling = false;
        return { modified: false, cart: cart };
      }

      var chain = Promise.resolve();
      gifts.forEach(function (g) { chain = chain.then(function () { return removeGift(g.key); }); });
      chain = chain.then(function () { return addGiftBatched(target, cart); });
      return chain.then(fetchCart).then(function (fresh) {
        isReconciling = false;
        return { modified: true, cart: fresh };
      }).catch(function (e) {
        console.error('[cart-manager] gift reconcile failed', e);
        isReconciling = false;
        return { modified: false, cart: cart };
      });

    }).catch(function (e) {
      console.error('[cart-manager] gift fetch failed', e);
      isReconciling = false;
      return { modified: false, cart: passedCart };
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     LINK GIFT RECONCILIATION — free cleanser for link visitors
     Qualifies = offer active + link code on the cart + an AA Baby Routine product.
     Keeps exactly 1 cleanser line (_tsa_link_gift = 1) while it qualifies;
     removes it as soon as it doesn't. Runs after the normal gift step.
  ═══════════════════════════════════════════════════════════════════════════ */

  function addLinkGift() {
    var vid = LINK_GIFT.giftVariantId
      ? Promise.resolve(LINK_GIFT.giftVariantId)
      : fetchVariantId(LINK_GIFT.giftHandle);
    return vid.then(function (variantId) {
      if (!variantId) throw new Error('link gift: no variant id');
      return fetch(Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _is_gift: 'true', _tsa_link_gift: '1' } }],
        }),
      }).then(function (r) { return r.json(); });
    });
  }

  function setLineQty(lineKey, qty) {
    return fetch(Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty }),
    }).then(function (r) { return r.json(); });
  }

  function reconcileLinkGift(prev) {
    var cart = prev.cart;
    if (!LINK_GIFT.enabled || isLinkReconciling || !cart || !cart.items) return Promise.resolve(prev);
    isLinkReconciling = true;

    var lines    = cart.items.filter(isLinkGiftItem);
    var hasPaid  = cart.items.some(function (i) { return !isGiftItem(i); });
    var codeOn   = cartHasLinkCode(cart);

    function finish(modified, fresh) {
      isLinkReconciling = false;
      return { modified: prev.modified || modified, cart: fresh || cart };
    }

    var qualifiesNow = !!(codeOn && hasPaid && offerActive(cart) && hasTriggerProduct(cart));

    return Promise.resolve(qualifiesNow).then(function (qualifies) {
      var chain = Promise.resolve();
      var touched = false;

      if (qualifies) {
        if (lines.length === 1 && lines[0].quantity === 1) return finish(false);
        if (lines.length >= 1) {
          /* keep the first line at qty 1, remove any extra lines */
          if (lines[0].quantity !== 1) {
            chain = chain.then(function () { return setLineQty(lines[0].key, 1); });
          }
          lines.slice(1).forEach(function (l) {
            chain = chain.then(function () { return setLineQty(l.key, 0); });
          });
          touched = true;
        } else {
          if (linkAddTries >= LINK_GIFT.maxTries) return finish(false);
          linkAddTries++;
          chain = chain.then(addLinkGift);
          touched = true;
        }
      } else {
        if (!lines.length) return finish(false);
        lines.forEach(function (l) {
          chain = chain.then(function () { return setLineQty(l.key, 0); });
        });
        touched = true;
      }

      return chain.then(fetchCart).then(function (fresh) { return finish(touched, fresh); });
    }).catch(function (e) {
      console.error('[cart-manager] link gift failed', e);
      return finish(false);
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     MAESTROO REFRESH
  ═══════════════════════════════════════════════════════════════════════════ */

  function dispatchMaestroRefresh() {
    if (refreshPending) { dbg('refresh skipped (already pending)'); return; }
    refreshPending = true;
    dbg('REFRESH drawer');
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER FUNCTIONS
  ═══════════════════════════════════════════════════════════════════════════ */

  // renderProgress — PAUSED (free shipping is site-wide, no tier bar needed)

  function giftSlotHTML(image, name, was) {
    var img = image
      ? '<img class="gift-tier__slot-img" src="' + esc(image) + '" alt="" loading="lazy">'
      : '<span class="gift-tier__slot-icon">🎁</span>';
    return '<div class="gift-tier__slot">'
        + '<div class="gift-tier__slot-row">'
          + img
          + '<div class="gift-tier__slot-info">'
            + '<span class="gift-tier__slot-label">FREE GIFT</span>'
            + '<span class="gift-tier__slot-name">' + esc(name) + '</span>'
          + '</div>'
          + '<div class="gift-tier__slot-price">'
            + '<span class="gift-tier__slot-was">' + was + '</span>'
            + '<span class="gift-tier__slot-free">\u20AC0</span>'
          + '</div>'
        + '</div>'
      + '</div>';
  }

  function euroComma(cents) {
    return '\u20AC' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function renderGiftSlots(cart) {
    var el = document.getElementById('gift-slots-display');
    if (!el) return;

    var gifts = cart.items.filter(isRegularGift);
    var links = cart.items.filter(isLinkGiftItem);
    if (!gifts.length && !links.length) { el.innerHTML = ''; return; }

    var html = '';
    if (gifts.length) {
      html += giftSlotHTML(gifts[0].image, 'Anti Aging Travel Mini Cream', '\u20AC10,00');
    }
    if (links.length) {
      html += giftSlotHTML(links[0].image, LINK_GIFT.giftName, euroComma(links[0].original_price));
    }
    el.innerHTML = html;
  }

  function renderManualCouponNotice(cart, manualCode, pairDetected, routine15Saving) {
    // Coupon strip UI removed — strip with ✕ close button no longer shown in drawer.
    var existing = document.getElementById('cm-manual-coupon-notice');
    if (existing) existing.remove();
    return;

    var saving;
    if (pairDetected && routine15Saving > 0 && cart.total_discount > 0) {
      var rawSubtotal   = cart.items.reduce(function (sum, item) {
        return isGiftItem(item) ? sum : sum + item.original_line_price;
      }, 0);
      var postRoutine15 = rawSubtotal - routine15Saving;
      var manualPct     = rawSubtotal > 0 ? cart.total_discount / rawSubtotal : 0;
      saving = Math.round(postRoutine15 * manualPct);
    } else {
      saving = cart.total_discount || 0;
    }
    injectStyles();

    var notice = document.createElement('div');
    notice.id  = 'cm-manual-coupon-notice';
    notice.innerHTML =
    '<div class="ue-notice">'
    + '<div class="ue-notice__left">'
    + '<span class="ue-notice__check">✓</span>'
    + '<div class="ue-notice__body">'
    + '<span class="ue-notice__code">' + esc(manualCode.toUpperCase()) + '</span>'
    + '<span class="ue-notice__desc"> applied at checkout</span>'
    + '</div>'
    + '</div>'
    + '<div style="display:flex;align-items:center;gap:.5rem;flex-shrink:0;">'
    + (saving > 0 ? '<span class="ue-notice__saving">−' + formatMoney(saving) + '</span>' : '')
    + '<button class="ue-notice__remove" type="button" aria-label="Remove coupon" title="Remove coupon">✕</button>'
    + '</div>'
    + '</div>';
    notice.querySelector('.ue-notice__remove').addEventListener('click', function () {
      applyCartDiscount('').then(fetchAndSync);
    });

    var anchor = document.getElementById('gift-slots-display');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(notice, anchor);
    }
  }

  // renderDiscountNotice — PAUSED (ROUTINE15 not active)

  function renderSavingsBreakdown(cart, manualCode) {
    var section = document.getElementById('tsa-savings-section');
    if (!section) return;

    var nonGiftItems = cart ? cart.items.filter(function (i) { return !isGiftItem(i); }) : [];
    if (!nonGiftItems.length) { section.style.display = 'none'; return; }
    section.style.display = '';

    var rawSubtotal      = nonGiftItems.reduce(function (s, i) { return s + i.original_line_price; }, 0);
    var shippingFree     = rawSubtotal >= SHIPPING_THRESHOLD;

    // Gift row — only when gift is in cart
    var giftRow = document.getElementById('tsa-savings-gift-row');
    var hasGift = cart.items.some(isRegularGift);
    var linkGiftValue = cart.items.reduce(function (s, i) {
      return isLinkGiftItem(i) ? s + i.original_line_price : s;
    }, 0);
    if (giftRow) giftRow.style.display = hasGift ? 'flex' : 'none';

    // Shipping — only free when cart meets the €50 threshold
    var shippingRow = document.getElementById('tsa-savings-shipping-row');
    if (shippingRow) shippingRow.style.display = shippingFree ? 'flex' : 'none';

    // Coupon row — only when a manual code is applied
    var couponRow   = document.getElementById('tsa-savings-coupon-row');
    var couponLabel = document.getElementById('tsa-savings-coupon-label');
    var couponAmt   = document.getElementById('tsa-savings-coupon-amount');
    var couponSaving = 0;
    if (couponRow) {
      var couponDisc = discountExclLink(cart);
      if (manualCode && couponDisc > 0) {
        couponSaving = couponDisc;
        /* App discount → friendly label instead of the code name (keeps APP10
           off the screen). Any other code (influencer etc.) shows its name. */
        var isAppCode = isAutoCouponLabel(manualCode);
        if (couponLabel) couponLabel.textContent = isAppCode ? AUTO_COUPON_LABEL : manualCode.toUpperCase();
        if (couponAmt)   couponAmt.textContent   = '\u2212' + formatMoney(couponSaving);

        /* −X% shown only for the app discount (text set from AUTO_COUPON_PCT,
           so the drawer never shows a stale %); (expira pronto) no longer
           used — the app discount doesn't expire */
        var pctEl    = couponRow.querySelector('.tsa-savings__coupon-pct');
        var expiryEl = couponRow.querySelector('.tsa-savings__coupon-expiry');
        if (pctEl && isAppCode) pctEl.textContent = '\u2212' + AUTO_COUPON_PCT + '%';
        if (pctEl)    pctEl.style.display    = isAppCode ? '' : 'none';
        if (expiryEl) expiryEl.style.display = 'none';

        couponRow.style.display = 'flex';
      } else {
        couponRow.style.display = 'none';
      }
    }

    // Total Ahorrado = gift value + shipping saving (only when qualified) + coupon saving
    var totalSaving = (hasGift ? 1000 : 0) + linkGiftValue + (shippingFree ? SHIPPING_CENTS : 0) + couponSaving;
    var totalEl = document.getElementById('cart-savings-display');
    if (totalEl) totalEl.textContent = '\u2212' + formatMoney(totalSaving);

    // Estimated shipping row — visible when cart is below the free-shipping threshold
    var shippingEstRow = document.getElementById('tsa-savings-shipping-est-row');
    if (shippingEstRow) shippingEstRow.style.display = shippingFree ? 'none' : 'flex';

    // Total a pagar — adds estimated shipping when below threshold so the customer
    // knows the real amount they will pay at checkout (cart API never includes shipping)
    var payableEl   = document.getElementById('tsa-savings-payable');
    var payableHint = document.getElementById('tsa-payable-hint');
    var payableTotal = cart.total_price + (shippingFree ? 0 : SHIPPING_CENTS);
    if (payableEl)   payableEl.textContent = formatMoney(payableTotal);
    if (payableHint) payableHint.style.display = shippingFree ? 'none' : 'inline';
  }

  function renderTotals(cart, pairDetected, routine15Saving) {
    var totalEl    = document.getElementById('cart-total-display');
    var labelEl    = document.getElementById('cart-total-label');
    var savingsRow = document.getElementById('cart-savings-row');
    var savingsEl  = document.getElementById('cart-savings-display');

    var rawSubtotal     = cart.items.reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.original_line_price;
    }, 0);
    var routine15InCart = isRoutine15InCart(cart);
    var manualCode      = getManualCode(cart);
    var postRoutine15   = rawSubtotal - routine15Saving;
    var manualDisc      = discountExclLink(cart);   // without the link cleanser's €0

    var displayedSub;
    var totalSaving = routine15Saving;

    if (routine15InCart) {
      displayedSub = cart.total_price;
    } else if (pairDetected && manualCode && manualDisc > 0 && rawSubtotal > 0) {
      var manualPct    = manualDisc / rawSubtotal;
      var manualSaving = Math.round(postRoutine15 * manualPct);
      displayedSub = postRoutine15 - manualSaving;
      totalSaving  = routine15Saving + manualSaving;
    } else if (pairDetected) {
      displayedSub = postRoutine15;
    } else if (manualCode && manualDisc > 0) {
      displayedSub = cart.total_price;
      totalSaving  = manualDisc;
    } else {
      displayedSub = cart.total_price;
    }

    // Add gift savings (retail value of free gift items via compare_at_price)
    var giftSavings = 0;
    cart.items.forEach(function (item) {
      if (isGiftItem(item)) giftSavings += item.original_price * item.quantity;
    });
    totalSaving += giftSavings;

    var displayTotal = displayedSub + (rawSubtotal >= SHIPPING_THRESHOLD ? 0 : SHIPPING_CENTS);

    if (totalEl) totalEl.textContent = formatMoney(displayTotal);
    var checkoutBtn = document.getElementById('tsa-checkout-full-btn');
    if (checkoutBtn) checkoutBtn.setAttribute('aria-label', 'Finalizar compra — ' + formatMoney(displayTotal));

    if (savingsRow && savingsEl) {
      if (totalSaving > 0) {
        savingsEl.textContent = '−' + formatMoney(totalSaving);
        savingsRow.style.display = 'flex';
      } else {
        savingsRow.style.display = 'none';
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     MASTER SYNC
  ═══════════════════════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════════════════════
     TRAVEL MINI RECONCILIATION
     Enforces the per-order cap (max 2, or the customer's remaining lifetime
     allowance if window._tsaMiniAllowance is set by product-info.liquid).
     Runs after gift reconciliation in every sync cycle.
  ═══════════════════════════════════════════════════════════════════════════ */

  function getMiniMax() {
    /* Read lifetime-adjusted allowance set by product-info.liquid on PDPs.
       Falls back to 2 on non-PDP pages (per-order max). */
    return typeof window._tsaMiniAllowance !== 'undefined'
      ? Math.max(0, parseInt(window._tsaMiniAllowance, 10))
      : 1;
  }

  function reconcileMinis(giftResult) {
    var cart = giftResult.cart;
    var max  = getMiniMax();

    var minis = (cart.items || []).filter(function (i) {
      return i.properties && i.properties['_travel_mini'] === '1';
    });

    if (minis.length <= max) {
      return Promise.resolve({ modified: giftResult.modified, cart: cart });
    }

    /* Remove excess minis (keep the first `max`, remove the rest) */
    var excess = minis.slice(max);
    var chain  = Promise.resolve();
    excess.forEach(function (item) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ id: item.key, quantity: 0 })
        }).then(function (r) { return r.json(); });
      });
    });

    return chain.then(fetchCart).then(function (fresh) {
      return { modified: true, cart: fresh };
    }).catch(function (e) {
      console.error('[cart-manager] mini reconcile failed', e);
      return { modified: giftResult.modified, cart: cart };
    });
  }

  function renderProgressBar(cart) {
    var wrap  = document.getElementById('tsa-progress-wrap');
    if (!wrap) return;

    var nonGift     = cart ? cart.items.filter(function (i) { return !isGiftItem(i); }) : [];
    var rawSubtotal = nonGift.reduce(function (s, i) { return s + i.original_line_price; }, 0);

    if (!cart || !nonGift.length) {
      wrap.style.display = 'none';
      return;
    }

    wrap.style.display = '';

    var pct      = Math.min(100, (rawSubtotal / MIN_PURCHASE_CENTS) * 100);
    var fillEl   = document.getElementById('tsa-progress-fill');
    var msgEl    = document.getElementById('tsa-progress-msg');
    var unlocked = rawSubtotal >= MIN_PURCHASE_CENTS;

    if (fillEl) fillEl.style.width = pct + '%';
    wrap.classList.toggle('is-unlocked', unlocked);

    if (msgEl) {
      if (unlocked) {
        msgEl.innerHTML = '<span class="tsa-progress__check">✓</span> ¡Regalo gratis + envío gratis desbloqueados!';
      } else {
        var remaining   = MIN_PURCHASE_CENTS - rawSubtotal;
        var euros       = Math.floor(remaining / 100);
        var cents       = remaining % 100;
        var remainAmt   = euros + (cents ? ',' + (cents < 10 ? '0' + cents : cents) : '') + '€';
        msgEl.innerHTML = 'Añade <strong>' + remainAmt + '</strong> más para 🎁 regalo + 🚚 envío gratis';
      }
    }
  }

  function renderAll(cart) {
    if (!cart || !cart.item_count) {
      // Empty cart — clear everything
      var slotsEl        = document.getElementById('gift-slots-display');
      var upsellEl       = document.getElementById('upsell-display');
      var noticeEl       = document.getElementById('cm-routine15-notice');
      var manualNoticeEl = document.getElementById('cm-manual-coupon-notice');
      var savingsEl      = document.getElementById('tsa-savings-section');
      var progressEl     = document.getElementById('tsa-progress-wrap');
      if (slotsEl)        slotsEl.innerHTML  = '';
      if (upsellEl)       upsellEl.innerHTML = '';
      if (noticeEl)       noticeEl.remove();
      if (manualNoticeEl) manualNoticeEl.remove();
      if (savingsEl)      savingsEl.style.display = 'none';
      if (progressEl)     progressEl.style.display = 'none';
      return;
    }

    var pairDetected    = detectPair(cart);
    var routine15Saving = pairDetected ? calcRoutine15Saving(cart) : 0;
    var manualCode      = getManualCode(cart);

    renderProgressBar(cart);
    renderGiftSlots(cart);
    renderManualCouponNotice(cart, manualCode, pairDetected, routine15Saving);
    renderSavingsBreakdown(cart, manualCode);
    renderTotals(cart, pairDetected, routine15Saving);
  }

  /**
   * Full sync cycle:
   * 1. Fetch cart
   * 2. ensureSourceAttribute + ensureDiscountState — tag cart, apply APP10 in
   *    the app / remove it (and NEW20 / APP20) in browsers
   * 3. Reconcile gifts — always 1 gift when cart has non-gift items
   * 4. If gifts changed → dispatch Maestroo refresh
   * 5. Else → render directly
   */
  /* True only when the current document IS the standalone Cart page —
     used to decide how to reflect an added/removed line-item row
     (gift or travel mini) after reconciliation. */
  function isOnCartPage() {
    var p = window.location.pathname;
    return p === '/cart' || p === (Shopify.routes.root + 'cart') || p === (Shopify.routes.root + 'cart/');
  }

  /* Reload guard for the Cart page when only the discount changed: at most one
     reload per 15 s, so a code that can't be applied can never cause a loop. */
  function safeReload() {
    try {
      var last = parseInt(sessionStorage.getItem('tsa_cm_reload') || '0', 10);
      if (Date.now() - last < 15000) return false;
      sessionStorage.setItem('tsa_cm_reload', String(Date.now()));
    } catch (e) {
      return false;
    }
    window.location.reload();
    return true;
  }

  /* One sync PASS: cart → discount → gifts → link gift → minis.
     Data work only, no drawer refresh. Resolves { modified, discountChanged, cart }. */
  function syncPass(cart) {
    var discountChanged = false;

    return ensureSourceAttribute(cart)
      .then(function (attrCart) {
        return primeOfferTags(attrCart).then(function () {
          return ensureDiscountState(attrCart);
        });
      })
      .then(function (discountResult) {
        discountChanged = discountResult.changed;
        return reconcileGifts(discountResult.cart, true);
      })
      .then(function (giftResult) {
        return reconcileLinkGift(giftResult);
      })
      .then(function (giftResult) {
        return reconcileMinis(giftResult);
      })
      .then(function (result) {
        return { modified: !!result.modified, discountChanged: discountChanged, cart: result.cart };
      });
  }

  /* Reflect the FINAL result of a sync in the UI — exactly one refresh. */
  function applySyncResult(modified, discountChanged, cart) {
    if (!modified && discountChanged) {
      /* Only the discount changed (APP10 added/removed). Line prices in
         the drawer are rendered by Liquid from the cart discount, so they
         need a redraw, not just renderAll(). */
      if (isOnCartPage()) {
        if (!safeReload()) renderAll(cart);
      } else {
        dispatchMaestroRefresh();
      }
      return;
    }

    if (modified) {
      /* A gift or travel-mini row was added/removed server-side.
         That requires a new/removed <line-item> row, which renderAll()
         cannot produce (it only repaints the progress bar, gift-slot
         widget, savings breakdown, and totals — never the item rows
         themselves). dispatchMaestroRefresh() normally handles this by
         asking Maestroo's CartDrawer to redraw and echo back
         'cart-drawer:refreshed' — but that drawer doesn't exist on the
         standalone Cart page, so that echo never arrives and renderAll()
         silently never runs, leaving the gift row and every downstream
         calculation stuck until a manual refresh.
         On the Cart page specifically, reload instead — it's the only
         way to get a correct new line-item row without duplicating
         Liquid's rendering logic in JS. Everywhere else (drawer context),
         keep the existing in-place refresh. */
      if (isOnCartPage()) {
        window.location.reload();
      } else {
        dispatchMaestroRefresh();
      }
    } else {
      renderAll(cart);
    }

  }

  /* Is the cleanser state of this cart consistent with the offer?
     wanted → cleanser line AND code present; not wanted → neither. */
  function linkStable(cart) {
    if (!LINK_GIFT.enabled || !cart || !cart.items) return true;
    var hasPaid = cart.items.some(function (i) { return !isGiftItem(i); });
    var wanted  = !!(hasPaid && offerActive(cart) && hasTriggerProduct(cart));
    var hasLine = cart.items.some(isLinkGiftItem);
    var hasCode = cartHasLinkCode(cart);
    return wanted ? (hasLine && hasCode) : (!hasLine && !hasCode);
  }

  /* Will this sync probably add something? (used to dim the drawer at once
     instead of showing a half-finished cart for a second) */
  function likelyWork(cart) {
    if (!cart || !cart.items) return false;
    var nonGift = cart.items.filter(function (i) { return !isGiftItem(i); });
    if (!nonGift.length) return false;
    var raw = nonGift.reduce(function (s, i) { return s + i.original_line_price; }, 0);
    if (raw >= MIN_PURCHASE_CENTS && !cart.items.some(isRegularGift)) return true;
    return !linkStable(cart);
  }

  var maskTimer = null;
  function setSyncMask(on) {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    clearTimeout(maskTimer);
    if (on) {
      drawer.classList.add('cart-drawer--refreshing');
      maskTimer = setTimeout(function () { drawer.classList.remove('cart-drawer--refreshing'); }, 5000);
    } else {
      drawer.classList.remove('cart-drawer--refreshing');
    }
  }

  function cartSignature(cart) {
    return (cart.items || []).filter(function (i) { return !isGiftItem(i); })
      .map(function (i) { return i.key + 'x' + i.quantity; }).join('|');
  }

  /* Full sync: runs passes until the cart is stable (max MAX_SYNC_PASSES), THEN
     refreshes the drawer once — so the mini gift, the code and the cleanser
     all appear together instead of one reload each. Only one sync runs at a
     time; anything that arrives meanwhile is coalesced into one quiet re-check. */
  function runSync(cart) {
    if (syncRunning && (Date.now() - syncStartedAt) < 20000) {
      syncQueued = true;
      dbg('sync queued (one already running)');
      return;
    }
    syncRunning   = true;
    syncStartedAt = Date.now();

    var anyModified = false;
    var anyDiscount = false;
    var lastCart    = cart;
    var n           = 0;

    function loop(c) {
      n++;
      return syncPass(c).then(function (r) {
        anyModified = anyModified || r.modified;
        anyDiscount = anyDiscount || r.discountChanged;
        lastCart    = r.cart || lastCart;
        dbg('pass', n, { modified: r.modified, discount: r.discountChanged });
        if ((r.modified || r.discountChanged) && !linkStable(r.cart) && n < MAX_SYNC_PASSES) {
          return fetchCart().then(loop);
        }
      });
    }

    /* Always work on the live cart — an event's cart can be stale after our own writes */
    fetchCart()
      .catch(function () { return cart; })
      .then(function (fresh) {
        /* a real change of the customer's products resets the retry caps */
        var sig = cartSignature(fresh);
        if (sig !== lastCartSig) {
          lastCartSig   = sig;
          autoApplyTries = 0;
          linkCodeTries  = 0;
          linkAddTries   = 0;
        }
        lastCart = fresh;
        if (likelyWork(fresh)) setSyncMask(true);
        return loop(fresh);
      })
      .then(function () {
        applySyncResult(anyModified, anyDiscount, lastCart);
      })
      .catch(function (e) {
        console.error('[cart-manager] sync failed', e);
      })
      .then(function () {
        /* a drawer refresh removes the dim itself ('cart-drawer:refreshed');
           if none was sent, remove it here */
        if (!refreshPending) setSyncMask(false);
        syncRunning = false;
        if (syncQueued) {
          syncQueued = false;
          dbg('running queued re-check');
          fetchAndSync();
        }
      });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     FAST ADD
  ═══════════════════════════════════════════════════════════════════════════ */

  /* The product shown on the current product page: handle, id, variant prices
     (cents) — from the page itself, no request. null on any other page. */
  function pdpProduct() {
    try {
      var m    = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (!m || !meta || !meta.variants || !meta.variants.length) return null;
      var vs = {};
      meta.variants.forEach(function (v) { vs[String(v.id)] = { price: Number(v.price) }; });
      return { handle: decodeURIComponent(m[1]).toLowerCase(), id: meta.id, variants: vs };
    } catch (e) { return null; }
  }

  function primeHandleTags(handle) {
    if (!LINK_GIFT.enabled || !LINK_GIFT.useTag || !handle) return;
    handle = handle.toLowerCase();
    if (tagStore[handle] || isOfferHandle(handle)) return;
    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { tagStore[handle] = ((p && p.tags) || []).map(function (x) { return String(x).toLowerCase(); }); })
      .catch(function () {});
  }

  /* Read a cart/add request body (JSON string, FormData or url-encoded) into
     { items:[{id,quantity,properties,selling_plan}], extras:{sections,sections_url}, single }.
     null = format not understood → leave the request alone. */
  function parseAddBody(body) {
    var obj = null, fd = null;
    if (typeof body === 'string') {
      try { obj = JSON.parse(body); } catch (e) {
        try { fd = new URLSearchParams(body); } catch (e2) { return null; }
      }
    } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
      fd = body;
    } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      fd = body;
    } else {
      return null;
    }

    if (obj) {
      if (!obj || typeof obj !== 'object') return null;
      var extrasJ = {};
      if (obj.sections)     extrasJ.sections     = obj.sections;
      if (obj.sections_url) extrasJ.sections_url = obj.sections_url;
      if (Array.isArray(obj.items)) {
        return { items: obj.items.map(function (i) { return Object.assign({}, i); }), extras: extrasJ, single: false };
      }
      if (obj.id === undefined) return null;
      var itemJ = { id: Number(obj.id), quantity: Number(obj.quantity || 1) };
      if (obj.properties)   itemJ.properties   = obj.properties;
      if (obj.selling_plan) itemJ.selling_plan = obj.selling_plan;
      return { items: [itemJ], extras: extrasJ, single: true };
    }

    /* form-style */
    var id = fd.get('id');
    if (!id || fd.get('items[0][id]')) return null;
    var item = { id: Number(id), quantity: Number(fd.get('quantity') || 1) };
    var props = {}, hasProps = false;
    fd.forEach(function (v, k) {
      var m = /^properties\[(.+)\]$/.exec(k);
      if (m) { props[m[1]] = v; hasProps = true; }
    });
    if (hasProps) item.properties = props;
    if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
    var extrasF = {};
    if (fd.get('sections'))     extrasF.sections     = fd.get('sections');
    if (fd.get('sections_url')) extrasF.sections_url = fd.get('sections_url');
    return { items: [item], extras: extrasF, single: true };
  }

  function addUrlMatches(input) {
    var u = typeof input === 'string' ? input : (input && input.url) || '';
    return /\/cart\/add(\.js)?(\?|#|$)/.test(u);
  }

  /* Decide which gifts belong in this add. Mirrors reconcileGifts /
     reconcileLinkGift on a PREDICTED cart (current cart + the item being
     added). A wrong guess is harmless: the normal sync right after corrects it. */
  function predictFastAdd(parsed, pdp, cart) {
    var added = [];
    for (var i = 0; i < parsed.items.length; i++) {
      var it = parsed.items[i];
      if (it.properties) {
        for (var k in it.properties) { if (k.charAt(0) === '_') return null; }
      }
      var v = pdp.variants[String(it.id)];
      if (!v || !(v.price > 0)) return null;
      var qty = Math.max(1, Number(it.quantity || 1));
      added.push({
        key: 'fastadd_' + it.id, variant_id: it.id, product_id: pdp.id,
        url: '/products/' + pdp.handle, properties: it.properties || {},
        quantity: qty, price: v.price, original_price: v.price,
        original_line_price: v.price * qty, final_line_price: v.price * qty
      });
    }

    var predicted = {
      items: (cart.items || []).concat(added),
      discount_codes: cart.discount_codes || []
    };
    return planGifts(cart, predicted);
  }

  /* Which gift lines belong with this cart? `cart` = the cart before the add,
     `predicted` = cart + the added item(s). Mirrors reconcileGifts /
     reconcileLinkGift; a wrong guess is corrected by the normal sync. */
  function planGifts(cart, predicted) {
    var giftItems = [];

    /* regular mini gift — only when there is none yet */
    var nonGift = predicted.items.filter(function (x) { return !isGiftItem(x); });
    var raw = nonGift.reduce(function (s, x) { return s + x.original_line_price; }, 0);
    var hasRegular = (cart.items || []).some(isRegularGift);
    if (!hasRegular && raw >= MIN_PURCHASE_CENTS) {
      var target = resolveGiftVariant(predicted);
      if (target) giftItems.push({ id: target, quantity: 1, properties: { _is_gift: 'true' } });
    }

    /* cleanser — offer active + qualifying product + variant id known */
    var wantLink = false;
    var lv = linkGiftVariantNow();
    if (LINK_GIFT.enabled && lv && !(cart.items || []).some(isLinkGiftItem) && !linkDeclined() &&
        offerActive(predicted) && hasTriggerProduct(predicted)) {
      giftItems.push({ id: lv, quantity: 1, properties: { _is_gift: 'true', _tsa_link_gift: '1' } });
      wantLink = true;
    }

    return { giftItems: giftItems, wantLink: wantLink };
  }

  /* Product added from somewhere we can't read the product from (collection
     page, quick-add …): 1) the theme's add runs untouched, 2) its answer tells
     us what was added, 3) gifts (+ coupon) are added, 4) only then the theme
     gets its answer — with fresh sections. Two quick requests instead of ten. */
  function reactiveAdd(native, input, init, parsed, cart, release) {
    function post(url, bodyObj) {
      return native(Shopify.routes.root + url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(bodyObj)
      });
    }

    /* while the theme's add runs, make sure the cleanser's variant id is known */
    var warmP = (LINK_GIFT.enabled && !linkGiftVariantNow() && offerActive(cart))
      ? fetchVariantId(LINK_GIFT.giftHandle).catch(function () {})
      : Promise.resolve();

    return Promise.all([native(input, init), warmP]).then(function (pair) {
      var res1 = pair[0];
      if (!res1.ok) { release(); return res1; }
      var copy;
      try { copy = res1.clone(); } catch (e) { release(); return res1; }

      return copy.json().then(function (json1) {
        var addedItems = (json1.items ? json1.items : [json1]).filter(function (x) { return x && x.key; });
        if (!addedItems.length) { release(); return res1; }

        var keys = {};
        addedItems.forEach(function (x) { keys[x.key] = 1; });
        var predicted = {
          items: (cart.items || []).filter(function (x) { return !keys[x.key]; }).concat(addedItems),
          discount_codes: cart.discount_codes || []
        };
        var plan = planGifts(cart, predicted);
        if (!plan.giftItems.length) {
          dbg('fast add (reactive): nothing to add');
          release();
          return res1;
        }
        dbg('fast add (reactive): adding', plan.giftItems.length, 'gift item(s)', plan.wantLink ? '+ code' : '');

        function secs(o) {
          if (parsed.extras.sections)     o.sections     = parsed.extras.sections;
          if (parsed.extras.sections_url) o.sections_url = parsed.extras.sections_url;
          return o;
        }

        return post('cart/add.js', secs({ items: plan.giftItems })).then(function (gres) {
          if (gres.ok) return gres;
          /* e.g. cleanser sold out → still give the mini gift */
          var onlyMini = plan.giftItems.filter(function (g) { return !(g.properties && g.properties._tsa_link_gift); });
          if (plan.wantLink && onlyMini.length) {
            plan.wantLink = false;
            dbg('gift add refused → mini gift only');
            return post('cart/add.js', secs({ items: onlyMini }));
          }
          return gres;
        }).then(function (gres) {
          if (!gres.ok) { release(); return res1; }
          return gres.json().then(function (gj) {
            var sections = gj.sections;
            var step = Promise.resolve(null);
            if (plan.wantLink) {
              var codes = (cart.discount_codes || [])
                .filter(function (d) { return !isOurCode(d.code) && !isLinkGiftCode(d.code); })
                .map(function (d) { return d.code; });
              codes.push(LINK_GIFT.codes[0]);
              step = post('cart/update.js', secs({ discount: codes.join(',') }))
                .then(function (r) { return r.ok ? r.json() : null; })
                .catch(function () { return null; });
            }
            return step.then(function (upd) {
              if (upd && upd.sections) sections = upd.sections;
              var out = Object.assign({}, json1);
              if (sections) out.sections = sections;
              knownCart = null; knownCartAt = 0;
              release();
              dbg('fast add (reactive) done');
              return new Response(JSON.stringify(out), {
                status: 200, statusText: 'OK', headers: { 'Content-Type': 'application/json' }
              });
            });
          });
        });
      }).catch(function (e) {
        /* the theme's add already happened — never re-send it, just hand back its answer */
        release();
        dbg('fast add (reactive) error after add', e && e.message);
        return res1;
      });
    }).catch(function (e) {
      release();
      dbg('fast add (reactive) error', e && e.message);
      throw e;
    });
  }

  function fastAddFetch(native, input, init) {
    if (!FAST_ADD.enabled || fastAddBusy) return null;
    if (!addUrlMatches(input)) return null;
    init = init || {};
    var method = String(init.method || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST' || (typeof Request !== 'undefined' && input instanceof Request)) return null;
    if (isAppContext()) return null;

    var parsed = parseAddBody(init.body);
    if (!parsed) { dbg('fast add skipped: request format not understood'); return null; }
    for (var pi = 0; pi < parsed.items.length; pi++) {
      var pp = parsed.items[pi].properties;
      if (pp) { for (var pk in pp) { if (pk.charAt(0) === '_') return null; } }   // our own / internal adds
    }
    var pdp = pdpProduct();   // null on collection pages etc. → reactive path below

    fastAddBusy = true;
    function release() { fastAddBusy = false; }

    var cartP = (knownCart && (Date.now() - knownCartAt) < FAST_ADD.maxKnownCartAgeMs)
      ? Promise.resolve(knownCart) : fetchCart();

    return cartP.then(function (cart) {
      var plan;
      try { plan = pdp ? predictFastAdd(parsed, pdp, cart) : null; }
      catch (e) { release(); dbg('fast add: planning failed → original request', e && e.message); return native(input, init); }
      if (plan === null) {
        /* can't predict from the page (collection page, quick-add, other product):
           let the theme's add go through, then add the gifts before answering */
        return reactiveAdd(native, input, init, parsed, cart, release);
      }
      if (!plan.giftItems.length) {
        dbg('fast add: nothing to add with the product');
        release();
        return native(input, init);
      }
      dbg('fast add: adding with product →', plan.giftItems.length, 'gift item(s)', plan.wantLink ? '+ code' : '');

      var payload = { items: parsed.items.concat(plan.giftItems) };
      if (parsed.extras.sections)     payload.sections     = parsed.extras.sections;
      if (parsed.extras.sections_url) payload.sections_url = parsed.extras.sections_url;

      var headers = new Headers(init.headers || {});
      headers.set('Content-Type', 'application/json');
      if (!headers.get('Accept')) headers.set('Accept', 'application/json');
      var modInit = Object.assign({}, init, { body: JSON.stringify(payload), headers: headers });

      return native(input, modInit).then(function (res) {
        if (!res.ok) {
          /* e.g. a gift is sold out → add the product alone, exactly as the theme asked */
          dbg('fast add refused → original request');
          release();
          return native(input, init);
        }
        var resCopy = null;
        try { resCopy = res.clone(); } catch (e) {}
        return res.json().then(function (addJson) {
          /* coupon right after the add (cart/add can't carry it) — with the same
             sections the theme asked for, so the drawer HTML already shows it */
          var codes = (cart.discount_codes || [])
            .filter(function (d) { return !isOurCode(d.code) && !isLinkGiftCode(d.code); })
            .map(function (d) { return d.code; });
          if (plan.wantLink) codes.push(LINK_GIFT.codes[0]);

          var needCode = plan.wantLink;
          var updP = Promise.resolve(null);
          if (needCode) {
            var ub = { discount: codes.join(',') };
            if (parsed.extras.sections)     ub.sections     = parsed.extras.sections;
            if (parsed.extras.sections_url) ub.sections_url = parsed.extras.sections_url;
            updP = native(Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify(ub)
            }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
          }

          return updP.then(function (upd) {
            knownCart = null; knownCartAt = 0;
            var itemsOut = addJson.items || [];
            var out;
            if (parsed.single) {
              out = Object.assign({}, itemsOut[0] || addJson);
            } else {
              out = Object.assign({}, addJson, { items: itemsOut.slice(0, parsed.items.length) });
            }
            var sec = (upd && upd.sections) || addJson.sections;
            if (sec) out.sections = sec;
            release();
            dbg('fast add done');
            return new Response(JSON.stringify(out), {
              status: 200, statusText: 'OK', headers: { 'Content-Type': 'application/json' }
            });
          });
        }).catch(function (e) {
          /* the add itself WORKED — never send it again, hand back what we have */
          release();
          dbg('fast add: error after add', e && e.message);
          if (resCopy) return resCopy;
          throw e;
        });
      });
    }, function (e) {
      /* could not even read the cart — nothing sent yet → the theme's own request */
      release();
      dbg('fast add error → original request', e && e.message);
      return native(input, init);
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     FAST REMOVE — gifts / coupon leave in the SAME request as the product (or
     the chip), so the drawer never shows a gift without its product.
  ═══════════════════════════════════════════════════════════════════════════ */

  /* cart/change | cart/update request body → plain object (or null = leave alone) */
  function readCartWriteBody(body) {
    var o = null;
    if (typeof body === 'string') {
      try { o = JSON.parse(body); } catch (e) {
        try { o = formToObject(new URLSearchParams(body)); } catch (e2) { return null; }
      }
    } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
      o = formToObject(body);
    } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      o = formToObject(body);
    }
    if (!o || typeof o !== 'object') return null;
    var allowed = { id: 1, line: 1, quantity: 1, updates: 1, discount: 1, sections: 1, sections_url: 1, form_type: 1, utf8: 1 };
    for (var k in o) { if (!allowed[k]) return null; }   // attributes / note / properties … → not ours
    return o;
  }

  function formToObject(fd) {
    var o = {}, updates = null;
    fd.forEach(function (v, k) {
      var m = /^updates\[(.+)\]$/.exec(k);
      if (m) { updates = updates || {}; updates[m[1]] = v; return; }
      o[k] = v;
    });
    if (updates) o.updates = updates;
    return o;
  }

  function cartWriteKind(input) {
    var u = typeof input === 'string' ? input : (input && input.url) || '';
    if (/\/cart\/change(\.js)?(\?|#|$)/.test(u)) return 'change';
    if (/\/cart\/update(\.js)?(\?|#|$)/.test(u))  return 'update';
    return null;
  }

  function lineKeyFor(cart, ref, byLine) {
    var items = cart.items || [];
    if (byLine) { var it = items[Number(ref) - 1]; return it ? it.key : null; }
    for (var i = 0; i < items.length; i++) { if (items[i].key === String(ref)) return items[i].key; }
    var hit = items.filter(function (x) { return String(x.variant_id) === String(ref); });
    return hit.length === 1 ? hit[0].key : null;
  }

  /* Cart would still qualify for the cleanser? Unknown tags count as "yes" so we
     never remove a cleanser on a guess. */
  function mayStillQualify(remaining) {
    return remaining.items.some(function (i) {
      if (isGiftItem(i)) return false;
      var h = extractHandle(i);
      if (!h) return true;
      if (itemQualifies(i)) return true;
      return LINK_GIFT.useTag && !tagStore[h.toLowerCase()] && !isOfferHandle(h);
    });
  }

  function fastCartWriteFetch(native, input, init) {
    if (!FAST_ADD.enabled || fastAddBusy) return null;
    var kind = cartWriteKind(input);
    if (!kind) return null;
    init = init || {};
    var method = String(init.method || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST' || (typeof Request !== 'undefined' && input instanceof Request)) return null;
    if (isAppContext() || !LINK_GIFT.enabled) return null;
    var hdr = init.headers;
    if (hdr && (hdr['X-TSA-Internal'] || (typeof hdr.get === 'function' && hdr.get('X-TSA-Internal')))) return null;

    var req = readCartWriteBody(init.body);
    if (!req) return null;

    /* what is the customer doing? */
    var removeRefs = null, byLine = false, chip = null;
    if (kind === 'change') {
      if (Number(req.quantity) !== 0) return null;
      if (req.line !== undefined)      { removeRefs = [req.line]; byLine = true; }
      else if (req.id !== undefined)   { removeRefs = [req.id]; }
      else return null;
    } else {
      if (req.discount !== undefined && req.updates === undefined) {
        chip = String(req.discount).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      } else if (req.updates !== undefined && req.discount === undefined) {
        var ups = req.updates, refs = [], allZero = true;
        if (Array.isArray(ups)) {
          ups.forEach(function (q, idx) { if (Number(q) === 0) refs.push(idx + 1); else if (Number(q) !== -1) allZero = false; });
          byLine = true;
        } else {
          Object.keys(ups).forEach(function (k) { if (Number(ups[k]) === 0) refs.push(k); else allZero = false; });
        }
        if (!allZero || !refs.length) return null;
        removeRefs = refs;
      } else {
        return null;
      }
    }

    fastAddBusy = true;
    function release() { fastAddBusy = false; }

    var cartP = (knownCart && (Date.now() - knownCartAt) < FAST_ADD.maxKnownCartAgeMs)
      ? Promise.resolve(knownCart) : fetchCart();

    return cartP.then(function (cart) {
      var updates = {};
      var newCodes = null;
      var otherCodesNow = (cart.discount_codes || [])
        .filter(function (d) { return !isOurCode(d.code) && !isLinkGiftCode(d.code); })
        .map(function (d) { return d.code; });
      var linkLines = (cart.items || []).filter(isLinkGiftItem);

      if (chip) {
        /* × on the code chip: requested list = the cart's codes minus the link code */
        var hadLink = cartHasLinkCode(cart);
        var reqHasLink = chip.some(isLinkGiftCode);
        var prevAll = (cart.discount_codes || []).map(function (d) { return codeUpper(d.code); });
        var subset = chip.every(function (c) { return prevAll.indexOf(codeUpper(c)) !== -1; });
        if (!hadLink || reqHasLink || !subset || chip.length !== prevAll.length - 1) { release(); return native(input, init); }
        setLinkDeclined(true);
        linkSeen = false;
        if (!linkLines.length) { release(); return native(input, init); }
        linkLines.forEach(function (l) { updates[l.key] = 0; });
        newCodes = chip;
        dbg('fast remove: code chip removed → cleanser leaves with it');
      } else {
        var keys = [];
        for (var i = 0; i < removeRefs.length; i++) {
          var k = lineKeyFor(cart, removeRefs[i], byLine);
          if (!k) { release(); return native(input, init); }
          keys.push(k);
        }
        var gone = {}; keys.forEach(function (k) { gone[k] = 1; updates[k] = 0; });
        var remaining = {
          items: (cart.items || []).filter(function (x) { return !gone[x.key]; }),
          discount_codes: cart.discount_codes || []
        };
        var paid = remaining.items.filter(function (x) { return !isGiftItem(x); });
        var extra = [];
        if (!paid.length) {
          remaining.items.forEach(function (x) { if (isRegularGift(x) || isLinkGiftItem(x)) extra.push(x); });
        } else {
          var raw = paid.reduce(function (s, x) { return s + x.original_line_price; }, 0);
          if (raw < MIN_PURCHASE_CENTS) remaining.items.forEach(function (x) { if (isRegularGift(x)) extra.push(x); });
          if (!mayStillQualify(remaining)) remaining.items.forEach(function (x) { if (isLinkGiftItem(x)) extra.push(x); });
        }
        if (!extra.length) { release(); return native(input, init); }   // nothing else to remove
        extra.forEach(function (x) { updates[x.key] = 0; });
        if (extra.some(isLinkGiftItem) && cartHasLinkCode(cart)) newCodes = otherCodesNow;
        dbg('fast remove: product leaves →', extra.length, 'gift line(s) leave with it');
      }

      var ub = { updates: updates };
      if (newCodes) ub.discount = newCodes.join(',');
      if (req.sections)     ub.sections     = req.sections;
      if (req.sections_url) ub.sections_url = req.sections_url;

      return native(Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(ub)
      }).then(function (res) {
        if (!res.ok) { release(); return native(input, init); }
        return res.json().then(function (out) {
          knownCart = (out && out.items) ? out : null;
          knownCartAt = Date.now();
          release();
          dbg('fast remove done');
          return new Response(JSON.stringify(out), {
            status: 200, statusText: 'OK', headers: { 'Content-Type': 'application/json' }
          });
        });
      });
    }).catch(function (e) {
      release();
      dbg('fast remove error → original request', e && e.message);
      return native(input, init);
    });
  }

  function installFastAdd() {
    if (!FAST_ADD.enabled || window.__tsaFastAdd || typeof window.fetch !== 'function') return;
    window.__tsaFastAdd = true;
    var native = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var p = null;
      try { p = fastAddFetch(native, input, init) || fastCartWriteFetch(native, input, init); } catch (e) { fastAddBusy = false; p = null; }
      return p || native(input, init);
    };

    /* warm what a fast add needs, as soon as a product page loads */
    var pdp = pdpProduct();
    if (pdp) {
      if (offerActive()) {
        primeHandleTags(pdp.handle);
        if (!LINK_GIFT.giftVariantId) fetchVariantId(LINK_GIFT.giftHandle).catch(function () {});
      }
    }
  }

  function fetchAndSync() {
    fetchCart()
      .then(runSync)
      .catch(function (e) { console.error('[cart-manager] fetch failed', e); });
  }

  // Expose for coupon apply in cart-drawer.liquid
  window._cartManager = { fetchAndSync: fetchAndSync };

  /* ═══════════════════════════════════════════════════════════════════════════
     EVENT BINDING
  ═══════════════════════════════════════════════════════════════════════════ */

  function bindUpsellAddEvent(container, handle) {
    var btn = container.querySelector('.ue-add-btn');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var self = this;
      if (self.disabled) return;
      self.disabled    = true;
      self.textContent = 'Adding...';

      fetchVariantId(handle)
        .then(function (variantId) {
          if (!variantId) throw new Error('no variant id');
          return fetch(Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
        })
        .then(fetchCart)
        .then(function (cart) {
          runSync(cart);
        })
        .catch(function () {
          self.textContent = 'Error — try again';
          self.disabled    = false;
        });
    });
  }

  /** ROUTINE15 paused — checkout always goes straight to /checkout */
  function bindCheckoutInterception() {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('button[name="checkout"]');
    if (!btn || !btn.closest('#cart-drawer')) return;

    // If cart is empty, let Maestroo handle it natively
    if (!btn.closest('form') || btn.disabled) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    fetchCart().then(function (cart) {
      // Guard: if cart is empty after checkout completion, just go to checkout natively
      if (!cart.item_count) {
        window.location.href = '/checkout';
        return;
      }
      window.location.href = '/checkout';
    }).catch(function () {
      window.location.href = '/checkout';
    });
  }, true);
}

  /* ═══════════════════════════════════════════════════════════════════════════
     MAESTROO EVENT LISTENERS
  ═══════════════════════════════════════════════════════════════════════════ */

  /* Shared handler — the drawer fires 'cart:change' on add/remove/quantity;
     the standalone Cart page's native <line-item> remove button and quantity
     selector instead fire 'cart:updated' for the same actions (confirmed by
     the skin-switcher's own re-init listener further down in line-item.liquid).
     Both must run the full reconcile chain — without this, removing an item
     or changing quantity on /cart updates the DOM row but leaves gifts,
     shipping progress, and savings stale until a hard refresh.

     Debounced: Maestroo can fire these events twice in quick succession for
     a single user action (an optimistic pre-fire + a confirmation fire).
     Coalescing avoids two overlapping reconcile cycles racing each other. */
  var cartEventDebounce = null;
  function handleMaestrooCartEvent(event) {
    var cart = event.detail && event.detail.cart;
    dbg('event', event.type);

    clearTimeout(cartEventDebounce);
    cartEventDebounce = setTimeout(function () {
      if (cart) {
        // Render immediately so gift slots etc. are visible
        // the moment the drawer opens — reconciliation may update again shortly after
        renderAll(cart);
        runSync(cart);
      } else {
        fetchAndSync();
      }
    }, 120);
  }

  document.addEventListener('cart:change',  handleMaestrooCartEvent);
  document.addEventListener('cart:updated', handleMaestrooCartEvent);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      isReconciling  = false;
      syncRunning    = false;
      syncQueued     = false;
      refreshPending = false;
      fetchCart().then(function (cart) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }).catch(function () {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
    }
  });

  var refreshedTimer = null;
  document.addEventListener('cart-drawer:refreshed', function () {
    var wasOurs = refreshPending;
    refreshPending = false;
    clearTimeout(refreshedTimer);
    refreshedTimer = setTimeout(function () {
      if (wasOurs) {
        fetchCart().then(renderAll).catch(function () {});
      } else {
        fetchAndSync();
      }
    }, 0);
  });

  /* ═══════════════════════════════════════════════════════════════════════════
     PDP UPSELL — PAUSED
     initPdpUpsell kept in file for easy re-enable; returns early.
  ═══════════════════════════════════════════════════════════════════════════ */

  function initPdpUpsell() {
    // PAUSED — upsell card hidden site-wide
    return;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     INIT
  ═══════════════════════════════════════════════════════════════════════════ */

  offerInit();          // session offer must be saved before the first cart sync
  installFastAdd();     // gifts + coupon travel with the product (product pages)
  bindCheckoutInterception();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      fetchAndSync();
      initPdpUpsell();
    });
  } else {
    fetchAndSync();
    initPdpUpsell();
  }

}());